import {
  Box,
  Button,
  Drawer,
  Stack,
  Typography,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Collapse,
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import { CombinedWarehouseOrderItemDto } from "@lib/ShiOneClient";
import { ReceiptLong } from "@mui/icons-material";
import { useMemo, useState, useEffect } from "react";
import { normalizeTransferIntegrationOrderStatus } from "@features/assets-feature/helpers/IntegrationCenterOrderHelpers";
import {
  warehouseStatuses,
  integrationStatuses,
} from "@features/assets-feature/helpers/WarehouseOrdersHelpers";
import { warehouseTypeColors } from "@features/assets-feature/utils/assetsColors";
import ColoredChips from "shared-ui/src/theme/extensions/ColoredChips";
import DrawerContentContainer from "shared-ui/src/components/detail-drawer/DrawerContentContainer";
import DrawerHeader from "shared-ui/src/components/detail-drawer/DrawerHeader";
import IntegrationCenterTrackingTransferIntegration from "./IntegrationCenterTrackingTransferIntegration";
import useGetWarehouseTracking from "@features/assets-feature/hooks/warehouse/useGetWarehouseTracking";
import { WarehouseOrderType } from "@features/assets-feature/constants";

const stepperStyles = {
  "& .MuiStep-root": { minHeight: "40px" },
  "& .Mui-completed + .MuiStepConnector-root .MuiStepConnector-line, & .Mui-completed .MuiStepContent-root":
    {
      borderColor: "secondary.main",
    },
  "& .MuiStepIcon-root.Mui-completed, & .shione-step-active .MuiStepLabel-iconContainer .MuiSvgIcon-root":
    {
      color: "secondary.main",
    },
};

export default function IntegrationCenterDetailDrawerTransferIntegration({
  open,
  onClose,
  onCloseAll,
  details,
  isStackedDrawer = false,
  zIndex,
}: Readonly<{
  open: boolean;
  onClose: () => void;
  onCloseAll?: () => void;
  details: CombinedWarehouseOrderItemDto | undefined;
  isStackedDrawer?: boolean;
  zIndex?: number;
}>) {
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const orderTypeColor = details?.orderType
    ? warehouseTypeColors[details.orderType]
    : undefined;
  const { trackingNumbers } = useGetWarehouseTracking(details);
  const orderSteps = useMemo(() => {
    const hasTracking = trackingNumbers && trackingNumbers.length > 0;
    const isTransfer =
      details?.orderType?.toLowerCase() ===
      WarehouseOrderType.pickNShip.toLowerCase();
    const orderStatus = normalizeTransferIntegrationOrderStatus(
      details?.orderStatus,
      details?.orderType,
    );
    const statuses = isTransfer ? warehouseStatuses : integrationStatuses;
    const stepNames = statuses.map((s) => s.name.toLowerCase());
    const currentStepIndex = stepNames.indexOf(orderStatus);
    const allTrackingNumbersComplete =
      hasTracking && trackingNumbers.every((tracking) => tracking.trackNum);
    const shippedStepIndex = stepNames.length - 1;

    let activeStepIndex = Math.max(0, currentStepIndex);
    if (currentStepIndex === shippedStepIndex && !allTrackingNumbersComplete) {
      activeStepIndex = shippedStepIndex;
    }

    const getStepStatus = (
      stepIndex: number,
    ): "completed" | "in-progress" | "pending" => {
      if (stepIndex === shippedStepIndex) {
        if (currentStepIndex > shippedStepIndex || allTrackingNumbersComplete) {
          return "completed";
        }
        if (currentStepIndex === shippedStepIndex) {
          return "in-progress";
        }
        return "pending";
      }

      if (stepIndex < activeStepIndex) return "completed";
      if (stepIndex === activeStepIndex) return "in-progress";
      return "pending";
    };

    const orderedDate =
      details?.confirmedDate?.toLocaleDateString("en-US") ||
      details?.receivedDate?.toLocaleDateString("en-US");
    const shipDate =
      trackingNumbers?.[0]?.shipDate?.toLocaleDateString("en-US");

    const stepLabels = statuses.map((status) => {
      const isOrdered = status.name === "Ordered";
      const isShipped = status.name === "Shipped";

      let date: string | undefined;
      if (isOrdered) {
        date = orderedDate;
      } else if (isShipped) {
        date = shipDate;
      }

      return {
        label: status.name,
        date,
      };
    });

    return {
      hasTracking,
      activeStepIndex,
      getStepStatus,
      stepLabels,
      isShipped: currentStepIndex === shippedStepIndex,
    };
  }, [details, trackingNumbers]);

  useEffect(() => {
    if (open && orderSteps.activeStepIndex >= 0) {
      setExpandedSteps(new Set([orderSteps.activeStepIndex]));
    }
  }, [open, orderSteps.activeStepIndex]);

  const toggleStep = (stepIndex: number) => {
    setExpandedSteps((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stepIndex)) {
        newSet.delete(stepIndex);
      } else {
        newSet.add(stepIndex);
      }
      return newSet;
    });
  };

  const renderContent = () => (
    <>
      <Box sx={{ p: 3 }}>
        {details && (
          <>
            <Typography variant="subtitle1" component="h2" sx={{ mb: 2 }}>
              Overview
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Sales Order
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {details.salesId ?? ""}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Purchase Order
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {details.purchOrder ?? ""}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Project
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {details.projectName ?? ""}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Destination
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {details.destination ?? ""}
                </Typography>
              </Grid>
            </Grid>
          </>
        )}
        {!details && (
          <Typography variant="body2" color="text.secondary" role="status">
            No order details available
          </Typography>
        )}
      </Box>

      <Divider />

      <Box sx={{ p: 3 }}>
        <Typography variant="subtitle1" component="h2" sx={{ mb: 3 }}>
          Order Status
        </Typography>
        <Box>
          <Stepper
            orientation="vertical"
            sx={stepperStyles}
            nonLinear
            aria-label="Order status timeline"
          >
            {orderSteps.stepLabels.map((stepLabel, index) => {
              const status = orderSteps.getStepStatus(index);
              const isActiveStep = index === orderSteps.activeStepIndex;
              const isExpanded = expandedSteps.has(index);

              return (
                <Step
                  key={`${stepLabel.label}-${index}`}
                  completed={status === "completed"}
                  expanded={isExpanded}
                  className={
                    status === "in-progress"
                      ? "shione-step-active"
                      : "shione-step-inactive"
                  }
                >
                  <StepLabel
                    onClick={() => isActiveStep && toggleStep(index)}
                    sx={{
                      cursor: isActiveStep ? "pointer" : "default",
                      pb: 0,
                    }}
                    {...(isActiveStep && {
                      "aria-expanded": isExpanded,
                      "aria-controls": `step-content-${index}`,
                    })}
                  >
                    <Stack>
                      <Typography variant="body2" fontWeight={500}>
                        {stepLabel.label === "Shipped" &&
                        !orderSteps.isShipped &&
                        stepLabel.date
                          ? "Estimated Ship Date"
                          : stepLabel.label}
                      </Typography>
                      {(stepLabel.date ||
                        (index === 0 &&
                          trackingNumbers &&
                          trackingNumbers.length > 1)) && (
                        <Typography variant="caption" color="text.secondary">
                          {stepLabel.date}
                          {index === 0 &&
                            trackingNumbers &&
                            trackingNumbers.length > 1 && (
                              <>
                                {stepLabel.date && " • "}
                                {trackingNumbers.length} shipments
                              </>
                            )}
                        </Typography>
                      )}
                    </Stack>
                  </StepLabel>
                  <StepContent>
                    <Collapse in={isExpanded}>
                      <Box
                        id={`step-content-${index}`}
                        role="region"
                        aria-label={`${stepLabel.label} details`}
                      >
                        {trackingNumbers?.map((tracking, idx) => (
                          <IntegrationCenterTrackingTransferIntegration
                            key={tracking.trackNum || `tracking-${idx}`}
                            isActiveStep={isActiveStep}
                            trackingNumbers={[tracking]}
                          />
                        ))}
                      </Box>
                    </Collapse>
                  </StepContent>
                </Step>
              );
            })}
          </Stepper>
        </Box>
      </Box>
    </>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" && onCloseAll) {
          onCloseAll();
        } else {
          onClose();
        }
      }}
      role="dialog"
      aria-label="Order Details"
      sx={{
        zIndex,
        "& .MuiDrawer-paper": {
          display: "flex",
          flexDirection: "column",
          ...(isStackedDrawer
            ? { height: "100vh", width: "50vw" }
            : { height: "100%" }),
        },
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.paper",
        }}
      >
        <DrawerHeader
          iconElement={
            <ReceiptLong sx={{ color: "text.secondary" }} aria-hidden="true" />
          }
          header={details?.uniqueIdentifier ?? ""}
          subheader={undefined}
          onClose={onClose}
          customRightHeaderElement={
            details?.orderType ? (
              <Box sx={{ display: "inline-flex", mr: 2 }}>
                <ColoredChips
                  borderColor={orderTypeColor}
                  label={details.orderType}
                />
              </Box>
            ) : undefined
          }
        />
      </Box>
      <Divider />
      {isStackedDrawer ? (
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            width: "100%",
            px: 3,
            py: 1.5,
          }}
        >
          {renderContent()}
        </Box>
      ) : (
        <DrawerContentContainer sx={{ flexGrow: 1, overflowY: "auto", pb: 0 }}>
          {renderContent()}
        </DrawerContentContainer>
      )}
      <Divider />
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 1,
          bgcolor: "table.header",
        }}
      >
        <Stack direction="row" justifyContent="flex-end" sx={{ p: 2 }}>
          <Button variant="text" onClick={onClose}>
            CLOSE
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
